import styled, { css } from 'styled-components'
import { Button } from '@components'
import { Link } from 'react-router-dom'

export const StyledHeader = styled.div`
  display: flex;
  width: 100%;
  gap: ${({ theme }) => theme.spacing.md}rem;
  padding: ${({ theme }) => theme.spacing.lg}rem;
  align-items: center;

  &.isMobile {
    gap: ${({ theme }) => theme.spacing.sm}rem;
    padding: ${({ theme }) => theme.spacing.md}rem;
  }
`

export const HeaderSpacer = styled.div`
  flex: 1;
`

const headerTitleStyles = css`
  margin: 0;
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  font-size: ${({ theme }) => theme.fontSize.md}rem;
  font-weight: 600;
  text-align: left;
  align-self: center;
`

export const HeaderTitle = styled.h1`
  ${headerTitleStyles}
  color: ${({ theme }) => theme.colors.neutralBlack};
`

export const HeaderTitleLink = styled(Link)`
  ${headerTitleStyles}
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs}rem;
  color: ${({ theme }) => theme.colors.primary};
  text-decoration: none;

  svg {
    flex-shrink: 0;
    width: ${({ theme }) => theme.iconSize.xs}rem;
    height: ${({ theme }) => theme.iconSize.xs}rem;
    fill: currentColor;
  }

  span {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &:hover {
    text-decoration: underline;
  }
`

export const RightMenuWrapper = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.sm}rem;
  align-items: center;
  flex: 0 0 auto;
`

const bookDemoActionStyles = css`
  position: relative;
  overflow: hidden;
  display: flex;
  border-radius: 100px;
  font-size: ${({ theme }) => theme.fontSize.md}rem;
  gap: ${({ theme }) => theme.spacing.xs}rem;
  align-items: center;
  padding: ${({ theme }) => theme.spacing.sm}rem ${({ theme }) => theme.spacing.md}rem;
  font-weight: 500;
  border: none;
  outline: none !important;
  background: transparent;
  color: ${({ theme }) => theme.colors.primary};
  white-space: nowrap;

  span {
    z-index: 1;
  }

  svg {
    z-index: 1;
    fill: ${({ theme }) => theme.colors.primary};
    min-width: ${({ theme }) => theme.iconSize.xs}rem;
    min-height: ${({ theme }) => theme.iconSize.xs}rem;
  }

  &.isMobile {
    & > span {
      display: none;
    }
  }
`

export const AvatarButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: 2.25rem;
  height: 2.25rem;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: transparent;
  cursor: pointer;
  transition: box-shadow 0.15s ease;

  span {
    font-size: ${({ theme }) => theme.fontSize.sm}rem;
    font-weight: 600;
  }

  &:hover {
    box-shadow: 0 0 0 3px ${({ theme }) => theme.dashboard.infoTint};
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px ${({ theme }) => theme.dashboard.infoTint};
  }
`

export const HamburgerButton = styled.button`
  border: none;
  color: ${({ theme }) => theme.colors.metalGray};
  background: none;
  flex: 0 0 auto;
  width: ${({ theme }) => theme.iconSize.lg}rem;
  height: ${({ theme }) => theme.iconSize.lg}rem;
`

export const StyledBookDemoButton = styled.button`
  ${bookDemoActionStyles}
  cursor: pointer;

  [data='book-demo-bg'] {
    opacity: 0.1;
  }

  &:hover {
    [data='book-demo-bg'] {
      opacity: 1;
    }
    svg,
    span {
      color: ${({ theme }) => theme.colors.neutralWhite};
      fill: ${({ theme }) => theme.colors.neutralWhite};
    }
  }
`

export const BookDemoDockSlot = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  flex-shrink: 0;
`

export const BookDemoSlotPlaceholder = styled.div`
  ${bookDemoActionStyles}
  visibility: hidden;
  pointer-events: none;
  user-select: none;
`

export const BookDemoSlotAnchor = styled.span`
  position: absolute;
  top: 50%;
  right: 0;
  width: 0;
  height: 0;
  transform: translateY(-50%);
  pointer-events: none;
`

export const BookDemoBg = styled.div.attrs({
  data: 'book-demo-bg'
})`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 0;
  ${({ theme }) => `
    background: linear-gradient(-45deg, ${theme.colors.primary}, ${theme.colors.success}, ${theme.colors.primary});
  `}
  background-size: 800% 800%;
  animation: bookDemoGradient 5s ease infinite;
  transition: 0.4s;

  @keyframes bookDemoGradient {
    0% {
      background-position: 0% 50%;
    }
    50% {
      background-position: 100% 50%;
    }
    100% {
      background-position: 0% 50%;
    }
  }
`

export const AIHelpButton = styled(Button)`
  flex: 0 0 auto;
  border: none;
  outline: none !important;
  transition: background 0.2s ease, color 0.2s ease;

  &&,
  &&:not(.is-secondary),
  &&.is-secondary {
    background: ${({ theme }) => theme.luca.navy};
    color: ${({ theme }) => theme.colors.neutralWhite};
  }

  &&.size-sm {
    border-radius: 100px;

    svg {
      width: 1rem;
      height: 1rem;
      min-width: 1rem;
      min-height: 1rem;
    }
  }

  svg {
    color: ${({ theme }) => theme.luca.sky};
    fill: ${({ theme }) => theme.luca.sky};
  }

  > span,
  > div {
    position: relative;
    z-index: 1;
  }

  &.isMobile {
    & > span {
      display: none;
    }
  }

  &:hover:not(.isOpen),
  &:focus:not(.isOpen) {
    filter: none;
    background: ${({ theme }) => theme.luca.deepBlue};
    color: ${({ theme }) => theme.colors.neutralWhite};

    svg,
    span {
      color: ${({ theme }) => theme.colors.neutralWhite};
      fill: ${({ theme }) => theme.luca.sky};
    }
  }

  &.isOpen {
    background: ${({ theme }) => theme.luca.deepBlue};
    color: ${({ theme }) => theme.colors.neutralWhite};
  }
`
